// src/components/PrintableReport.js - Fixed Version V14.1
import React, { forwardRef } from 'react';
import styles from './PrintableReport.module.css';
import { formatNumber } from '../../utils/formatters';

const PrintableReport = forwardRef(({ reportData, filters, settings }, ref) => {
    // التأكد من أن البيانات المطلوبة موجودة قبل العرض
    if (!reportData || !settings) {
        return null;
    }

    // --- الدوال المساعدة ---
    const getClientNameForBooking = (bookingId) => {
        const booking = reportData.bookings_details.find(b => b.id === bookingId);
        return booking?.client?.name || '- عام -';
    };

    const getStatusInArabic = (status) => {
        const statusMap = {
            'all': 'كل الحالات', 'completed': 'مكتمل', 'in_progress': 'قيد التنفيذ',
            'confirmed': 'مؤكد', 'pending': 'قيد الانتظار', 'cancelled': 'ملغي'
        };
        return statusMap[status] || status;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'غير محدد';
        try {
            return new Date(dateStr).toLocaleString('ar-IQ', {
                year: 'numeric', month: 'long', day: 'numeric'
            });
        } catch (error) { return dateStr; }
    };

    // --- منطق حساب الإجماليات ---
    const { summary } = reportData;

    // إجماليات جدول الحجوزات
    const bookingsTotal = reportData.bookings_details.reduce((sum, b) => sum + parseFloat(b.total_price || 0), 0);
    const bookingsPaid = reportData.bookings_details.reduce((sum, b) => sum + parseFloat(b.total_paid || 0), 0);
    const bookingsExpenses = reportData.bookings_details.reduce((sum, b) => sum + parseFloat(b.total_booking_expenses || 0), 0);
    const bookingsRemaining = reportData.bookings_details.reduce((sum, b) => sum + parseFloat(b.remaining_amount || 0), 0);

    // فلترة المصاريف العامة وحساب إجماليها
    const generalExpenses = reportData.all_expenses_details.filter(e => !e.booking);
    const expensesTotal = generalExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);


    return (
        <div ref={ref} className={styles.printContainer}>

            {/* ✨ الهيدر والبنر ✨ */}
            <header className={styles.printHeader}>
                {settings.banner && (
                    <img src={settings.banner} alt="Company Banner" className={styles.bannerImage} />
                )}
            </header>

            {/* ✨ المحتوى الرئيسي ✨ */}
            <main className={styles.printContent}>
                <h1 className={styles.mainTitle}>لوحة المعلومات المالية</h1>

                <div className={styles.filterInfo}>
                    <span><strong>من:</strong> {filters.start_date ? formatDate(filters.start_date) : 'البداية'}</span>
                    <span><strong>إلى:</strong> {filters.end_date ? formatDate(filters.end_date) : 'النهاية'}</span>
                    <span><strong>الحالة:</strong> {getStatusInArabic(filters.status)}</span>
                    <span><strong>تاريخ التقرير:</strong> {new Date().toLocaleDateString('ar-IQ')}</span>
                </div>

                {/* ✨ قسم الملخص المالي ✨ */}
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>📊 الملخص المالي</h2>
                    <div className={styles.financialGrid}>
                        {/* ... بطاقات الملخص تبقى كما هي ... */}
                        <div className={`${styles.summaryCard} ${styles.revenue}`}>
                            <span className={styles.cardTitle}>📈 إجمالي الإيرادات</span>
                            <span className={styles.cardValue}>{formatNumber(summary.total_revenue, 0)} د.ع</span>
                        </div>
                        <div className={`${styles.summaryCard} ${styles.received}`}>
                            <span className={styles.cardTitle}>📥 إجمالي المستلم</span>
                            <span className={styles.cardValue}>{formatNumber(summary.total_received, 0)} د.ع</span>
                        </div>
                        <div className={`${styles.summaryCard} ${styles.remaining}`}>
                            <span className={styles.cardTitle}>⏳ إجمالي المتبقي</span>
                            <span className={styles.cardValue}>{formatNumber(summary.total_remaining, 0)} د.ع</span>
                        </div>
                        <h3 className={styles.gridSeparator}>تفاصيل المصاريف</h3>
                        <div className={`${styles.summaryCard} ${styles.expense}`}>
                            <span className={styles.cardTitle}>💸 مصاريف الحجوزات</span>
                            <span className={styles.cardValue}>{formatNumber(summary.total_booking_expenses, 0)} د.ع</span>
                        </div>
                        <div className={`${styles.summaryCard} ${styles.expense}`}>
                            <span className={styles.cardTitle}>🛒 المصاريف العامة</span>
                            <span className={styles.cardValue}>{formatNumber(summary.total_general_expenses, 0)} د.ع</span>
                        </div>
                        <div className={`${styles.summaryCard} ${styles.totalExpense}`}>
                            <span className={styles.cardTitle}>🧾 إجمالي المصاريف</span>
                            <span className={styles.cardValue}>{formatNumber(summary.total_expenses, 0)} د.ع</span>
                        </div>
                        <div className={`${styles.summaryCard} ${styles.netProfit}`}>
                            <span className={styles.cardTitle}>💰 صافي الربح</span>
                            <span className={styles.cardValue}>{formatNumber(summary.net_profit, 0)} د.ع</span>
                        </div>
                    </div>
                </section>

                {/* ✨ قسم تفاصيل الحجوزات ✨ */}
                <section className={styles.tableSection} style={{
                    pageBreakInside: 'auto',
                    marginBottom: '12px'
                }}>
                    <h2 className={styles.sectionTitle}>📅 تفاصيل الحجوزات ({reportData.bookings_details.length} حجز)</h2>
                    {reportData.bookings_details.length > 0 ? (
                        <div className={styles.tableContainer}>
                            <table className={styles.reportTable}>
                                {/* ... محتوى جدول الحجوزات يبقى كما هو ... */}
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>اسم العميل</th>
                                        <th>نوع الحجز</th>
                                        <th>تاريخ الحجز</th>
                                        <th>الحالة</th>
                                        <th>السعر الإجمالي</th>
                                        <th>المدفوع</th>
                                        <th>مصاريف الحجز</th>
                                        <th>المتبقي</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.bookings_details.map((booking, index) => (
                                        <tr key={booking.id}>
                                            <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                            <td>{booking.client?.name || 'عميل محذوف'}</td>
                                            <td>{booking.booking_type || '-'}</td>
                                            <td>{booking.booking_date || '-'}</td>
                                            <td>{getStatusInArabic(booking.status)}</td>
                                            <td className={styles.amountCell}>{formatNumber(booking.total_price, 0)}</td>
                                            <td className={styles.amountCell}>{formatNumber(booking.total_paid, 0)}</td>
                                            <td className={styles.amountCell}>{formatNumber(booking.total_booking_expenses, 0)}</td>
                                            <td className={styles.amountCell} style={{ color: parseFloat(booking.remaining_amount) > 0 ? '#b45309' : '#166534' }}>
                                                {formatNumber(booking.remaining_amount, 0)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="5"><strong>الإجمالي الكلي للحجوزات:</strong></td>
                                        <td className={styles.amountCell}><strong>{formatNumber(bookingsTotal, 0)}</strong></td>
                                        <td className={styles.amountCell}><strong>{formatNumber(bookingsPaid, 0)}</strong></td>
                                        <td className={styles.amountCell}><strong>{formatNumber(bookingsExpenses, 0)}</strong></td>
                                        <td className={styles.amountCell} style={{ color: '#b45309' }}>
                                            <strong>{formatNumber(bookingsRemaining, 0)}</strong>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    ) : (<p style={{ textAlign: 'center', padding: '20px' }}>لا توجد حجوزات تطابق الفلاتر المحددة.</p>)}
                </section>

                {/* 
                    ✨ قسم تفاصيل المصاريف العامة - التعديل هنا ✨
                    سيتم عرض هذا القسم فقط في حالة عدم اختيار نوع حجز معين من الفلتر
                */}
                {!filters.booking_type && (
                    <section className={styles.tableSection} style={{
                        pageBreakInside: 'auto',
                        marginBottom: '100px'
                    }}>
                        <h2 className={styles.sectionTitle}>💰 تفاصيل المصاريف العامة ({generalExpenses.length} مصروف)</h2>
                        {generalExpenses.length > 0 ? (
                            <div className={styles.tableContainer}>
                                <table className={styles.reportTable}>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>حساب المصروف</th>
                                            <th>البيان / الوصف</th>
                                            <th>الحجز المرتبط</th>
                                            <th>تاريخ الصرف</th>
                                            <th>المبلغ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {generalExpenses.map((expense, index) => (
                                            <tr key={expense.id}>
                                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                                <td>{expense.category_name || 'غير محدد'}</td>
                                                <td>{expense.description || '-'}</td>
                                                <td>- عام -</td>
                                                <td>{expense.expense_date || '-'}</td>
                                                <td className={styles.amountCell} style={{ color: '#b91c1c' }}>{formatNumber(expense.amount, 0)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="5"><strong>الإجمالي الكلي للمصاريف العامة:</strong></td>
                                            <td className={styles.amountCell} style={{ color: '#b91c1c' }}>
                                                <strong>{formatNumber(expensesTotal, 0)}</strong>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        ) : (<p style={{ textAlign: 'center', padding: '20px' }}>لا توجد مصاريف عامة في هذه الفترة.</p>)}
                    </section>
                )}


                <div style={{ height: '20px', pageBreakAfter: 'avoid' }}></div>
            </main>

            {/* ✨ الفوتر ✨ */}
            <footer className={styles.printFooter}>
                <div className={styles.footerContent}>
                    {settings.company_name && <p>{settings.company_name}</p>}
                    {settings.phone_number && <p>{settings.phone_number}</p>}
                    {settings.address && <p>{settings.address}</p>}
                </div>
            </footer>
        </div>
    );
});

PrintableReport.displayName = 'PrintableReport';
export default PrintableReport;
